import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import ProjectManager from './ProjectManager';
import SkillsManager from './SkillsManager';
import CVManager from './CVManager';
import HeroManager from './HeroManager';
import AboutManager from './AboutManager';
import ResearchManager from './ResearchManager';
import ServicesManager from './ServicesManager';
import ImpactManager from './ImpactManager';
import TimelineManager from './TimelineManager';

const Dashboard = () => {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('projects');

  const tabs = [
    { id: 'projects', label: 'Projects', icon: '📁', desc: 'Manage portfolio projects' },
    { id: 'skills', label: 'Skills', icon: '🎯', desc: 'Technical capabilities' },
    { id: 'research', label: 'Research', icon: '🔬', desc: 'Research focus areas' },
    { id: 'services', label: 'Services', icon: '🛠️', desc: 'What I offer' },
    { id: 'impact', label: 'Impact', icon: '🌍', desc: 'Impact areas' },
    { id: 'timeline', label: 'Timeline', icon: '📅', desc: 'Career timeline' },
    { id: 'hero', label: 'Hero', icon: '🏠', desc: 'Hero section content' },
    { id: 'about', label: 'About', icon: '👤', desc: 'About section content' },
    { id: 'cv', label: 'CV', icon: '📄', desc: 'CV upload & management' },
  ];

  const handleLogout = () => {
    logout();
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'projects': return <ProjectManager />;
      case 'skills': return <SkillsManager />;
      case 'research': return <ResearchManager />;
      case 'services': return <ServicesManager />;
      case 'impact': return <ImpactManager />;
      case 'timeline': return <TimelineManager />;
      case 'hero': return <HeroManager />;
      case 'about': return <AboutManager />;
      case 'cv': return <CVManager />;
      default: return <ProjectManager />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <nav className="bg-slate-900/90 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-sm font-bold text-white">A</div>
              <div>
                <h1 className="text-lg font-bold text-white">
                  Admin <span className="gradient-text">Dashboard</span>
                </h1>
                <p className="text-xs text-slate-500 font-mono">Content Management</p>
              </div>
            </motion.div>

            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleTheme}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-600 text-slate-400 hover:text-white hover:border-slate-400 transition-all text-base"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </motion.button>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleLogout}
                className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-4 py-2 rounded-lg transition-all border border-red-500/20 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-thin">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02, y: -1 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-cyan-500/20 to-blue-600/20 text-cyan-300 border border-cyan-500/30 shadow-lg shadow-cyan-500/5'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <span className="text-base">{tab.icon}</span>
                <span>{tab.label}</span>
              </motion.button>
            ))}
          </div>
          {/* Tab description */}
          <p className="text-xs text-slate-500 font-mono mt-3">
            {tabs.find(t => t.id === activeTab)?.desc || ''}
          </p>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-800 p-6 sm:p-8 shadow-xl"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-600 font-mono">
          Obert Manyasa — Admin Panel v2.0
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
